import React, { useMemo } from 'react';
import { getLocalStorage } from '@shared/core';
import { CurrencySelectorV2 } from '@shared/components/CurrencySelectorV2';
import { useGeoBasedLocale, CURRENCY_PARAMS, isValidCurrencyCode, updateLocaleCurrency, updateLocaleSearchParams } from '@shared/hooks/useGeoLocale';
import { isTravelCurrencyCode } from '@shared/utils/currency';
import { withWindow } from '@shared/utils/withWindow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useCurrencyCodeFromBrowserLocale } from '@shared/utils/currency/useCurrencyCodeFromBrowseLocale';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { isCurrencyCodeType } from '@shared/utils/currency/currency.constants';
import { TRAVEL_DEFAULT_CURRENCY_CODES } from './travelCurrencies';
import type { TravelCurrencySelectorProps, TravelCurrencyCodesPayload } from './TravelCurrencySelector.types';
import type { CurrencyCodeType } from '@shared/utils/currency/currency.types';

// Dedicated storage key for travel currency — separate from joyLocale so
// the locale sync pipeline (useI18nLocaleSyncer) doesn't overwrite non-legacy currencies.
export const TRAVEL_CURRENCY_STORAGE_KEY = 'joyTravelCurrency';

const filterValidCurrencyCodes = (value: unknown): ReadonlyArray<CurrencyCodeType> | undefined => {
  if (!Array.isArray(value)) return undefined;
  const valid = value.filter(isCurrencyCodeType);
  return valid.length > 0 ? valid : undefined;
};

export const parseCurrencyPayload = (raw: unknown): TravelCurrencyCodesPayload | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  return {
    popularCurrencyCodes: filterValidCurrencyCodes(obj.popularCurrencyCodes),
    allCurrencyCodes: filterValidCurrencyCodes(obj.allCurrencyCodes),
  };
};

const useTravelCurrency = (): CurrencyCodeType => {
  const { currency: localeCurrency } = useGeoBasedLocale();
  const { getValueString } = useQueryParamHelper();

  // URL param takes highest priority — allows shared links to override stored preference
  const urlCurrency = getValueString(CURRENCY_PARAMS);
  if (urlCurrency && isTravelCurrencyCode(urlCurrency)) {
    return urlCurrency as CurrencyCodeType;
  }

  const stored = getLocalStorage().getItem(TRAVEL_CURRENCY_STORAGE_KEY);
  if (stored && isTravelCurrencyCode(stored)) {
    return stored as CurrencyCodeType;
  }

  return localeCurrency;
};

export const TravelCurrencySelector: React.FC<TravelCurrencySelectorProps> = ({
  isEnabled,
  mode = 'button',
  value: controlledValue,
  onChange: controlledOnChange,
  disabled,
  placeholder,
}) => {
  const internalCurrency = useTravelCurrency();
  const browserCurrency = useCurrencyCodeFromBrowserLocale();
  const [, isFFLoading, , flagPayload] = useIsFeatureEnabled('travelCurrencies');

  const currencyPayload = useMemo(() => parseCurrencyPayload(flagPayload), [flagPayload]);

  const payloadPopularCodes = currencyPayload?.popularCurrencyCodes;
  const payloadAllCodes = currencyPayload?.allCurrencyCodes;

  const allCurrencyCodes = payloadAllCodes ?? TRAVEL_DEFAULT_CURRENCY_CODES;

  // Build popular codes: browser-locale currency first, then the payload/default popular list, deduplicated
  const popularCurrencyCodes = useMemo((): ReadonlyArray<CurrencyCodeType> => {
    const allCodesSet = new Set<CurrencyCodeType>(allCurrencyCodes);
    const basePopularCodes = payloadPopularCodes ?? TRAVEL_DEFAULT_CURRENCY_CODES;
    const codes: CurrencyCodeType[] = [];
    const seen = new Set<CurrencyCodeType>();

    if (browserCurrency && allCodesSet.has(browserCurrency as CurrencyCodeType)) {
      codes.push(browserCurrency as CurrencyCodeType);
      seen.add(browserCurrency as CurrencyCodeType);
    }

    for (const code of basePopularCodes) {
      if (!seen.has(code)) {
        codes.push(code);
        seen.add(code);
      }
    }

    return codes;
  }, [browserCurrency, payloadPopularCodes, allCurrencyCodes]);

  const currencies = useMemo(
    () => ({
      popularCurrencyCodes,
      allCurrencyCodes,
    }),
    [popularCurrencyCodes, allCurrencyCodes],
  );

  const handleInternalChange = useEventCallback((currencyCode: CurrencyCodeType) => {
    getLocalStorage().setItem(TRAVEL_CURRENCY_STORAGE_KEY, currencyCode);

    if (isValidCurrencyCode(currencyCode)) {
      updateLocaleCurrency(currencyCode);
    } else {
      updateLocaleSearchParams({ currency: currencyCode }, { addParam: 'currency' });
    }

    withWindow(global => global.location.reload());
  });

  if (!isEnabled || isFFLoading) return null;

  return (
    <CurrencySelectorV2<CurrencyCodeType>
      value={controlledValue ?? internalCurrency}
      onChange={controlledOnChange ?? handleInternalChange}
      mode={mode}
      hideFlag
      disabled={disabled}
      placeholder={placeholder}
      currencies={currencies}
    />
  );
};

TravelCurrencySelector.displayName = 'TravelCurrencySelector';
